import { useDispatch, useSelector } from "react-redux";
import {
  BreadcrumbArrow,
  EmptyListIcon,
  FilterIcon,
  HomeIcon,
  PoleIcon,
  SummaryDashboardIcon,
} from "../../assets/icons/Icons";
import {
  getListOfOrders,
  getPriorityIcon,
  getStatusDetails,
} from "./CurrentScheduleView";
import { getTimeEpochToUTC } from "../Utils/HelperFunctions";
import {
  setHighlightedOrderAssign,
  setScheduleDashboardView,
  setShowReviewSchedulePopup,
} from "../../../../store/UnifiedControlPlaneSlice";
import UnifiedControlPlaneService from "../../UnifiedControlPlane.service";
import { Fragment, useEffect, useState } from "react";
import FilterPopup from "./FilterPopup";
import { ucp_visualization_presentation } from "../CesiumUtils/Visualizations";
import ScheduleSummary from "./ScheduleSummary";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";

const ScheduleDashboard = ({ mission_id, ucpViewer }) => {
  const dispatch = useDispatch();
  const orderAssignments = useSelector(
    (state) => state.unifiedControlPlane.orderAssignments
  );
  const highlightedOrderAssign = useSelector(
    (state) => state.unifiedControlPlane.highlightedOrderAssign
  );
  const previewScheduleId = useSelector(
    (state) => state.unifiedControlPlane.previewScheduleId
  );
  const schedules = useSelector((state) => state.unifiedControlPlane.schedules);
  const ordersData = useSelector((state) => state.unifiedControlPlane.orders);
  const allOrders = [
    ...ordersData.executed,
    ...ordersData.current,
    ...ordersData.pending,
  ];

  const [filteredResult, setFilteredResult] = useState(orderAssignments);

  const [showScheduleView, setShowScheduleView] = useState("");
  const [selectedSatellites, setSelectedSatellites] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [satelliteNames, setSatelliteNames] = useState([]);
  const [orderNames, setOrderNames] = useState([]);
  const [groundStationNames, setGroundStationNames] = useState([]);
  const [selectedGroundStations, setSelectedGroundStations] = useState([]);

  const setOrderAssignmentActive = (item) => {
    // COMPLETED ==> green highlighted & NOT-COMPLETED ==> white highlighted
    const whiteHighilightRgba = [255, 255, 255, 255];
    const greenHighlightRbga = [204, 245, 78, 255];
    dispatch(setHighlightedOrderAssign(item));
    const highlightColor =
      item.state === "CaptureComplete" || item.state === "DownlinkComplete"
        ? greenHighlightRbga
        : whiteHighilightRgba;
    if (item?.type === "Order") {
      ucp_visualization_presentation().highlight_strip(
        ucpViewer,
        item?.data,
        "Highlight_Order_Assignment",
        highlightColor
      );
    }
  };

  const getStripsOfNewOrderAssignments = (order) => {
    const listOfNewStrips = [];
    order.forEach((assignment, index) => {
      if (
        assignment.state === "New"
      ) {
        listOfNewStrips.push({
          name: index,
          areaOfInterestId: index,
          polygon: assignment.data.strip.geoJSON.coordinates,
        });
      }
    });
    return listOfNewStrips;
  };

  const goToOrderDashboard = () => {
    dispatch(setScheduleDashboardView(false));
    dispatch(setShowReviewSchedulePopup(""));
    ucp_visualization_presentation().reset_all_aois_visibility(ucpViewer)
  };

  const goToScheduleListView = () => {
    dispatch(setScheduleDashboardView(false));
    dispatch(UnifiedControlPlaneService.getAllSchedules(mission_id));
    dispatch(setShowReviewSchedulePopup("list-schedule"));
    ucp_visualization_presentation().reset_all_aois_visibility(ucpViewer)
  };

  const showFilterPopup = () => {
    if (showScheduleView !== "filter-view") {
      setShowScheduleView("filter-view");
    } else {
      setShowScheduleView("");
    }
  };

  const showSummaryDashboard = () => {
    if (showScheduleView !== "summary-view") {
      setShowScheduleView("summary-view");
    } else {
      setShowScheduleView("");
    }
  };

  const getUniqueNamesOrdersAndSatellites = () => {
    const orders = [
      ...new Set(
        (orderAssignments || [])
          .flatMap((item) => {
            if (!item || !item.type || !item.data) return [];

            if (item.type === "Order") {
              return item.data.orderSummary?.orderName
                ? [item.data.orderSummary.orderName]
                : [];
            }

            if (item.type === "Ground") {
              return Array.isArray(item.data.downlinkOrders)
                ? item.data.downlinkOrders
                  .map((order) => order?.orderName)
                  .filter(Boolean)
                : [];
            }

            return [];
          })
          .filter(Boolean)
      ),
    ];
    const satellites = [
      ...new Set(
        (orderAssignments || [])
          .map((item) => item?.data?.satellite?.name)
          .filter(Boolean)
      ),
    ];
    const groundStations = [
      ...new Set(
        (orderAssignments || [])
          .filter((item) => item?.type === "Ground")
          .map((item) => item?.data?.groundStation?.name)
          .filter(Boolean)
      ),
    ];
    return { orders, satellites, groundStations };
  };

  const getUniqueIdsOrdersAndSatellites = () => {
    const orderIds = [
      ...new Set(
        (orderAssignments || [])
          .flatMap((item) => {
            if (!item || !item.type || !item.data) return [];

            if (item.type === "Order") {
              return item.data.orderSummary?.orderId
                ? [item.data.orderSummary.orderId]
                : [];
            }

            if (item.type === "Ground") {
              return Array.isArray(item.data.downlinkOrders)
                ? item.data.downlinkOrders
                  .map((order) => order?.orderId)
                  .filter(Boolean)
                : [];
            }

            return [];
          })
          .filter(Boolean)
      ),
    ];
    const satelliteIds = [
      ...new Set(
        (orderAssignments || [])
          .map((item) => item?.data?.satellite?.name)
          .filter(Boolean)
      ),
    ];
    return { orderIds, satelliteIds };
  };

  const drawAOISforOrders = () => {
    const { orderIds } = getUniqueIdsOrdersAndSatellites();
    const filteredOrders = allOrders.filter((order) =>
      orderIds.includes(order.orderId)
    ).map((order) => {
      if (order?.spec?.geofeature?.features?.[0]?.id) {
        return {
          ...order,
          spec: {
            ...order.spec,
            geofeature: {
              ...order.spec.geofeature,
              features: order.spec.geofeature.features.map((feature) => ({
                ...feature,
                id: `aoi_${feature.id}`
              }))
            }
          }
        }
      }

      return order;
    })
    ucp_visualization_presentation().highlight_aois(
      filteredOrders,
      ucpViewer,
    );
  };

  useEffect(() => {
    const { orders, satellites, groundStations } =
      getUniqueNamesOrdersAndSatellites();
    drawAOISforOrders();
    setOrderNames(orders);
    setSatelliteNames(satellites);
    setSelectedOrders(orders);
    setSelectedSatellites(satellites);
    setGroundStationNames(groundStations);
    setSelectedGroundStations(groundStations);
    ucp_visualization_presentation().highlightCompletedAssignment(
      ucpViewer,
      "Highlight_Completed_Order_Assignment",
      getStripsOfNewOrderAssignments(orderAssignments),
      [255, 255, 255, 50]
    );

    return () => {
      ucp_visualization_presentation().remove_highlight_strip(
        ucpViewer,
        "Highlight_Order_Assignment"
      );
      ucp_visualization_presentation().remove_highlight_strip(
        ucpViewer,
        "Highlight_Order_AOIs"
      );
      ucp_visualization_presentation().remove_highlight_strip(
        ucpViewer,
        "Highlight_Completed_Order_Assignment"
      );
      // dispatch(ucpActions.resetOrderAssignmentsForSingleOrder());
    };
  }, [orderAssignments]);

  const getScheduleData = () => {
    return schedules?.find((s) => s.scheduleId === previewScheduleId);
  };

  const scheduleUpdateSuccessCallback = () => {
    dispatch(UnifiedControlPlaneService.getOrders());
    dispatch(UnifiedControlPlaneService.getCurrentSchedule(mission_id));
  };

  const updateSchedule = (action) => {
    const scheduleData = {
      scheduleId: previewScheduleId,
    };
    dispatch(
      UnifiedControlPlaneService.updateSchedules(
        mission_id,
        action,
        scheduleData,
        scheduleUpdateSuccessCallback
      )
    );
    goToOrderDashboard();
  };

  return (
    <div className="scheduleDashboard">
      <div className="schedule-dashboard-header">
        <div className="schedule-dashboard-header-left">
          <div className="schedule-home-icon" onClick={goToOrderDashboard}>
            <HomeIcon />
          </div>
          <BreadcrumbArrow />
          <div className="draft-text" onClick={goToScheduleListView}>
            {getScheduleData()?.state === "Current" ? "Current" : "Drafts"}
          </div>
          <BreadcrumbArrow color="#CCF54E" />
          <div title={getScheduleData()?.label} className="schedule-name limit-text">
            {getScheduleData()?.label}
          </div>
        </div>
        <div className="schedule-dashboard-header-right">
          <div className="schedule-filter-icon" onClick={showSummaryDashboard}>
            <SummaryDashboardIcon />
            <span className="ucpTooltip-below">Summary Dashboard</span>
          </div>
          <div className="schedule-filter-icon" onClick={showFilterPopup}>
            <FilterIcon color={"#CCF54E"} />
            <span className="ucpTooltip-below">Filter</span>
          </div>
        </div>

        {showScheduleView === "filter-view" && (
          <FilterPopup
            data={orderAssignments}
            selectedOrders={selectedOrders}
            setSelectedOrders={setSelectedOrders}
            selectedSatellites={selectedSatellites}
            setSelectedSatellites={setSelectedSatellites}
            satelliteNames={satelliteNames}
            orderNames={orderNames}
            groundStationNames={groundStationNames}
            selectedGroundStations={selectedGroundStations}
            setSelectedGroundStations={setSelectedGroundStations}
            onApply={(filtered) => {
              setFilteredResult(filtered);
              setShowScheduleView("");
            }}
          />
        )}
        {showScheduleView === "summary-view" && (
          <ScheduleSummary
            onClose={() => setShowScheduleView("")}
            scheduleData={getScheduleData()}
          />
        )}
      </div>
      {filteredResult?.length > 0 ? (
        <Fragment>
          <div className="order-assignments-list">
            {filteredResult?.length > 0 &&
              filteredResult.map((item, index) => (
                <div
                  key={index}
                  className="os-list-item"
                  onClick={() => setOrderAssignmentActive(item)}
                >
                  <div
                    className={
                      item?.assignmentId ===
                        highlightedOrderAssign?.assignmentId
                        ? "os-list-header-bar active-header-bar noTopPadding "
                        : "os-list-header-bar black-header-bar noTopPadding"
                    }
                    style={{ margin: "0px", display: "flex" }}
                  >
                    <CustomToolTip title={getStatusDetails(item?.state, 'Text')}>
                      <button className="os-status-btn">
                        {getStatusDetails(item?.state, 'Icon')}
                      </button>
                    </CustomToolTip>
                    <div className="" style={{ alignContent: "center" }}>
                      {item?.data?.band === "Imager" ? (
                        <img
                          className="band-icon"
                          src={require("../../assets/raw/DbIcon.svg").default}
                          alt=""
                        />
                      ) : (
                        <img
                          className="band-icon"
                          src={
                            require("../../assets/raw/SatelliteTabs.svg").default
                          }
                          alt=""
                        />
                      )}
                    </div>
                    <div className="os-title-main">
                      <div className="os-title">
                        <div className="os-sub-title grey limit-text">
                          {item?.type === "Order" ? (
                            <span
                              title={
                                item?.data?.orderSummary?.orderName
                                  ? item?.data?.orderSummary?.orderName
                                  : "-"
                              }
                            >
                              <img
                                className="order_details_spacing"
                                src={getPriorityIcon(
                                  item?.data?.orderSummary?.priorityLane
                                )}
                                alt="order-details-spacing"
                              />
                              {item?.data?.orderSummary?.orderName
                                ? item?.data?.orderSummary?.orderName
                                : "-"}
                            </span>
                          ) : (
                            <div className="orders-wrapper">
                              <span
                                className="orders_text"
                                title={`${item?.data?.downlinkOrders
                                  ? item?.data?.downlinkOrders.length
                                  : "All"
                                  } Orders`}
                              >
                                <span className="order_details_spacing">
                                  {item?.data?.downlinkOrders
                                    ? item?.data?.downlinkOrders.length
                                    : "All"}
                                </span>
                                Orders
                                <span className="orders_tooltip">
                                  {getListOfOrders(item?.data?.downlinkOrders)}
                                </span>
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="divider-bar">
                          <PoleIcon />
                        </span>
                        <div
                          className="os-sub-title grey limit-text"
                          title={item?.data?.satellite?.name}
                        >
                          {item?.data?.satellite?.name}
                        </div>
                      </div>
                      <div className="os-title">
                        {/* <div className="os-sub-title grey">{item?.data?.band ? item?.data?.band : item?.data?.ground_station?.name}&ensp;</div> */}
                        <div className="os-sub-title yellow os-label">
                          {item?.type === "Order"
                            ? item?.data?.type
                            : item?.label}
                        </div>
                      </div>
                    </div>
                    <div style={{ alignContent: "center", marginLeft: "auto" }}>
                      <div className="os-sub-title grey os-date-text">
                        {getTimeEpochToUTC(item?.startEpochSec)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          {getScheduleData()?.state === 'Draft' &&
            <div className="schedule-dashboard-footer">
              <button
                className="schedule-dashboard-btns ps-btn-dark"
                onClick={() => updateSchedule("Reject")}
              >
                Reject
              </button>
              <button
                className="schedule-dashboard-btns ps-btn-light"
                onClick={() => updateSchedule("Approve")}
              >
                Approve
              </button>
            </div>
          }
        </Fragment>
      ) : (
        <div className="scheduleDescription">
          <EmptyListIcon />
          <div className="scheduleDescription-text-box">
            <span className="scheduleDescription-text">No Data Available</span>
            <span className="scheduleDescription-subtext">
              Apply filters to view data
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleDashboard;
